import { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, HeartPulse } from 'lucide-react';
import { useBrainStore } from '@/state/brainStore';
import type { CustomerRecord } from '@/domain/types';

interface Props {
  open: boolean;
  onClose: () => void;
  customer: CustomerRecord;
  /** Name stamped as `lastUpdatedBy` on save (the acting persona). */
  updatedBy?: string;
}

/** Split a comma/newline-separated field into trimmed, non-empty entries. */
const parseList = (raw: string): string[] =>
  raw
    .split(/[\n,]/)
    .map((s) => s.trim())
    .filter(Boolean);

/**
 * Owner/manager-only editor for a customer's medical profile. The clinical
 * lists are entered as comma-separated text; the store owns the audit stamp
 * (`lastUpdatedAt` / `lastUpdatedBy`) on save.
 */
export default function MedicalProfileModal({ open, onClose, customer, updatedBy }: Props) {
  const updateMedicalProfile = useBrainStore((s) => s.updateMedicalProfile);
  const mp = customer.medicalProfile;

  const [allergies, setAllergies] = useState((mp?.allergies ?? []).join(', '));
  const [medications, setMedications] = useState((mp?.medications ?? []).join(', '));
  const [conditions, setConditions] = useState((mp?.conditions ?? []).join(', '));
  const [notes, setNotes] = useState(mp?.notes ?? '');

  const save = () => {
    updateMedicalProfile(
      customer.id,
      {
        allergies: parseList(allergies),
        medications: parseList(medications),
        conditions: parseList(conditions),
        notes: notes.trim() || undefined,
      },
      updatedBy,
    );
    onClose();
  };

  return (
    <AnimatePresence>
      {open ? (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 z-50 bg-overlay/80 backdrop-blur-sm flex items-center justify-center px-6"
          onClick={onClose}
        >
          <motion.div
            initial={{ y: 8, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            exit={{ y: 8, opacity: 0 }}
            transition={{ duration: 0.2 }}
            className="bg-ink-deep border border-gold-dim/40 rounded-xl w-full max-w-md p-6 relative"
            onClick={(e) => e.stopPropagation()}
            role="dialog"
            aria-label="Edit medical profile"
            data-testid="medical-profile-modal"
          >
            <button
              type="button"
              onClick={onClose}
              className="absolute top-3 right-3 text-cream-soft hover:text-cream"
              aria-label="Close"
            >
              <X size={16} />
            </button>
            <p className="label-small-caps text-gold-bright">Customer record</p>
            <h2 className="heading-display text-2xl text-cream mt-2 flex items-center gap-2">
              <HeartPulse size={20} className="text-blood" /> Medical profile
            </h2>
            <p className="font-inter text-[11px] text-cream-faint uppercase tracking-widest mt-2">
              Comma-separated · leave blank if none
            </p>

            <label className="block mt-5">
              <span className="label-small-caps">Allergies</span>
              <input
                type="text"
                value={allergies}
                onChange={(e) => setAllergies(e.target.value)}
                placeholder="e.g. latex, lidocaine"
                className="mt-2 w-full bg-ink-black border border-gold-dim/40 rounded-md px-3 py-2 text-sm text-cream focus:outline-none focus:border-gold-bright"
                data-testid="medical-profile-allergies"
              />
            </label>

            <label className="block mt-3">
              <span className="label-small-caps">Medications</span>
              <input
                type="text"
                value={medications}
                onChange={(e) => setMedications(e.target.value)}
                placeholder="e.g. antibiotics"
                className="mt-2 w-full bg-ink-black border border-gold-dim/40 rounded-md px-3 py-2 text-sm text-cream focus:outline-none focus:border-gold-bright"
                data-testid="medical-profile-medications"
              />
            </label>

            <label className="block mt-3">
              <span className="label-small-caps">Conditions</span>
              <input
                type="text"
                value={conditions}
                onChange={(e) => setConditions(e.target.value)}
                placeholder="e.g. diabetic, pregnant, blood-thinner"
                className="mt-2 w-full bg-ink-black border border-gold-dim/40 rounded-md px-3 py-2 text-sm text-cream focus:outline-none focus:border-gold-bright"
                data-testid="medical-profile-conditions"
              />
            </label>

            <label className="block mt-3">
              <span className="label-small-caps">Notes</span>
              <textarea
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                rows={3}
                placeholder="Anything the artist should know"
                className="mt-2 w-full bg-ink-black border border-gold-dim/40 rounded-md px-3 py-2 text-sm text-cream focus:outline-none focus:border-gold-bright resize-none"
                data-testid="medical-profile-notes"
              />
            </label>

            <div className="flex gap-2 mt-5 justify-end">
              <button type="button" onClick={onClose} className="btn-ghost text-xs">
                Cancel
              </button>
              <button
                type="button"
                onClick={save}
                className="btn-primary text-xs"
                data-testid="medical-profile-save"
              >
                Save medical profile
              </button>
            </div>
          </motion.div>
        </motion.div>
      ) : null}
    </AnimatePresence>
  );
}
